import React, { useState, useEffect } from 'react';
import Form from '../../coreComponents/Form/Form.tsx';
import { getDriverDetails, updateDriver } from '../../services/DriverService.ts';
import { TDriverDetails } from './type.ts';
import { Snackbar, Alert, Box, Typography } from '@mui/material';

interface DriverData {
  name: string;
  gender: string;
  dob: string | Date;
  location: string;
  preferredLanguage: string;
  vehicleType: string;
  vehicleNo: string;
  ownershipType: string;
  fuelType: string;
  registrationCertificate: string;
  drivingLicenseNo: string;
  licenseExpiry: string | Date;    
  driving_license: string;
  profile_photo: string;
}

interface UpdateDriverProps {
  id: string;
  onClose: () => void;
  onUpdateClose: () => void;
}

const UpdateDriver: React.FC<UpdateDriverProps> = ({ id, onClose, onUpdateClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [initialValues, setInitialValues] = useState<DriverData>({
    name: '',
    gender: '',
    dob: '',
    location: '',
    preferredLanguage: '',
    vehicleType: '',
    vehicleNo: '',
    ownershipType: '',
    fuelType: '',
    registrationCertificate: '',
    drivingLicenseNo: '',
    licenseExpiry: '',
    driving_license: '',
    profile_photo: '',
  });

  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        const response = await getDriverDetails(Number(id));
        console.log("response", response);
        const data: TDriverDetails = response.data;
        
        // Map the API data to form fields
        const formData = {
          name: data.driver.name,
          gender: data.driver.gender,
          dob: data.driver.dob ? new Date(data.driver.dob).toISOString().split('T')[0] : '',
          location: data.driver.location,
          preferredLanguage: data.driver.preferred_language,
          vehicleType: data.driver.vehicles[0]?.vehicle_type || '',
          vehicleNo: data.driver.vehicles[0]?.vehicle_no || '',
          ownershipType: data.driver.vehicles[0]?.ownership_type || '',
          fuelType: data.driver.vehicles[0]?.fuel_type || '',
          registrationCertificate: data.driver.registration_certificate || '',
          drivingLicenseNo: data.driver.driving_license_no,
          licenseExpiry: data.driver.license_expiry ? new Date(data.driver.license_expiry).toISOString().split('T')[0] : '',
          driving_license: data.driver.driving_license || '',
          profile_photo: data.driver.profile_photo || '',
        };
        
        setInitialValues(formData);
      } catch (err) {
        setError('Failed to load driver details');
        setSnackbar({
          open: true,
          message: 'Failed to load driver details',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDriverData();
    }
  }, [id]);

  const handleSubmit = async (data: any) => {
    try {
      console.log("data", data);
      // Create FormData for file uploads
      const formData = new FormData();
      
      // Format dates to yyyy-MM-dd only if they were changed
      if (data.dob && data.dob !== initialValues.dob) {
        const dobDate = new Date(data.dob);
        formData.append('dob', dobDate.toISOString().split('T')[0]);
      }
      if (data.licenseExpiry && data.licenseExpiry !== initialValues.licenseExpiry) {
        const expiryDate = new Date(data.licenseExpiry);
        formData.append('licenseExpiry', expiryDate.toISOString().split('T')[0]);
      }

      // Add files if they exist
      if (data.registrationCertificate) formData.append('rc', data.registrationCertificate);
      if (data.driving_license) formData.append('dl', data.driving_license);
      if (data.profile_photo) formData.append('pp', data.profile_photo);

      // Add other fields if they have values
      Object.keys(data).forEach(key => {
        if (key !== 'registrationCertificate' && key !== 'driving_license' && key !== 'profile_photo' && 
            key !== 'dob' && key !== 'licenseExpiry' && data[key]) {
          formData.append(key, data[key]);
        }
      });

      const response = await updateDriver(Number(id), formData);
      
      console.log("response", response);

      if (response.data) {
        setSnackbar({
          open: true,
          message: 'Driver updated successfully',
          severity: 'success',
        });
        onUpdateClose();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error updating driver',
        severity: 'error',
      });
    }
  };

  const fields = [
    { type: 'text', name: 'name', label: 'Name', required: true },
    { type: 'dropdown', name: 'gender', label: 'Gender', required: true,
        options: [
            { value: 'Men', label: 'Men' },
            { value: 'Women', label: 'Women' },
            { value: 'Not Specified', label: 'Not Specified' }
          ]
     },
    { type: 'date', name: 'dob', label: 'DOB', required: true },
    { type: 'dropdown', name: 'location', label: 'Location', required: true,
        options: [
            { value: 'Thiruvananthapuram', label: 'Thiruvananthapuram' },
            { value: 'Kollam', label: 'Kollam' },
            { value: 'Pathanamthitta', label: 'Pathanamthitta' },
            { value: 'Alappuzha', label: 'Alappuzha' },
            { value: 'Kottayam', label: 'Kottayam' },
            { value: 'Idukki', label: 'Idukki' },
            { value: 'Ernakulam', label: 'Ernakulam' },
            { value: 'Thrissur', label: 'Thrissur' },
            { value: 'Palakkad', label: 'Palakkad' },
            { value: 'Malappuram', label: 'Malappuram' },
            { value: 'Kozhikode', label: 'Kozhikode' },
            { value: 'Wayanad', label: 'Wayanad' },
            { value: 'Kannur', label: 'Kannur' },
            { value: 'Kasargod', label: 'Kasargod' },
          ]
     },
    { type: 'dropdown', name: 'preferredLanguage', label: 'Preferred Language', required: true,  multiple: true,
        options: [
            { value: 'English', label: 'English' },
            { value: 'Malayalam', label: 'Malayalam' },
            { value: 'Hindi', label: 'Hindi' },
            { value: 'Tamil', label: 'Tamil' },
        ]
     },
    { type: 'text', name: 'vehicleType', label: 'Vehicle Type', required: true },
    { type: 'text', name: 'vehicleNo', label: 'Vehicle No', required: true },
    { type: 'dropdown', name: 'ownershipType', label: 'Vehicle Ownership Type', required: true,
        options: [
            { value: 'SELF_OWNED', label: 'Self Owned' },
            { value: 'RENTED', label: 'Rented' }
          ]
     },
    { type: 'dropdown', name: 'fuelType', label: 'Vehicle Fuel Type', required: true,
        options: [
            { value: 'ELECTRIC', label: 'ELECTRIC' },
            { value: 'DIESEL', label: 'DIESEL' },
            { value: 'PETROL', label: 'PETROL' },
            { value: 'CNG', label: 'CNG' },
          ]
     },
    { type: 'image', name: 'registrationCertificate', label: 'Registration Certificate', required: true },
    { type: 'text', name: 'drivingLicenseNo', label: 'Driving License No', required: true },
    { type: 'date', name: 'licenseExpiry', label: 'Driving License Expiry', required: true },
    { type: 'image', name: 'driving_license', label: 'Driving License', required: true },
    { type: 'image', name: 'profile_photo', label: 'Profile Photo', required: true },
  ];

  const buttons = [
    {
      id: 'submit-button',
      children: 'Update Driver',
      type: 'submit' as 'submit',
      variant: 'contained' as 'contained',
      fullWidth: false,
    },
    {
      id: 'cancel-button',
      children: 'Cancel',
      type: 'button' as 'button',
      variant: 'contained' as 'contained',
      fullWidth: false,
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading driver details...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Update Driver
      </Typography>
      <Form
        fields={fields}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={onClose}
        buttons={buttons}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpdateDriver; 